import React, { useState, useEffect } from 'react';
import './Dashboard.css';

import DiarioModule from './modules/diario/DiarioModule';
import DiarioVeiculosModule from './modules/diario-veiculos/DiarioVeiculosModule';
import CadastroModule from './modules/cadastro/CadastroModule';
import PatioModule from './modules/patio/PatioModule';
import SuporteModule from './modules/suporte/SuporteModule';
import RhModule from './modules/rh/Rh';
import MonitoramentoModule from './modules/monitoramento/MonitoramentoModule';
import MonitoramentoVolkswagenModule from './modules/monitoramentoVolkswagen/MonitoramentoVolkswagenModule';
import RotogramaModule from './modules/rotograma/RotogramaModule';
import ChecklistModule from './modules/checklist';
import WMSModule from './modules/wms/WMSModule';
import PortariaModule from './modules/portaria/PortariaModule';
import ControlTowerModule from './modules/controltower/ControlTowerModule';
import LevantamentoPosicoesModule from './modules/levantamento-posicoes/LevantamentoPosicoesModule';
import EtlBisModule from './modules/etl-bis/EtlBisModule';
import VeiculosmModule from './modules/veiculosm/VeiculosmModule';
import AbastecimentoModule from './modules/abastecimento/AbastecimentoModule';
import ApisModule from './modules/apis/ApisModule';
import BIModule from './modules/bi/BIModule';

interface Module {
  id: string;
  name: string;
  icon: string;
  enabled: boolean;
}

interface User {
  id: number;
  username: string;
  nome?: string;
  nm_acesso?: string;
  nm_setor?: string;
  modules: string[];
}

interface DashboardProps {
  user: User;
  onLogout: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ user, onLogout }) => {
  const [modules, setModules] = useState<Module[]>([]);
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const [sidebarVisible, setSidebarVisible] = useState(true);

  useEffect(() => {
    const availableModules: Module[] = [
      { id: 'cadastro', name: 'Cadastro', icon: '👥', enabled: false },
      { id: 'diario', name: 'Diário de Bordo', icon: '📋', enabled: false },
      { id: 'diarioVeiculos', name: 'Diário de Veículos', icon: '🚛', enabled: false },
      { id: 'patio', name: 'Pátio', icon: '🏭', enabled: false },
      { id: 'rh', name: 'RH', icon: '🧑‍💼', enabled: false },
      { id: 'suporte', name: 'Suporte', icon: '🛠️', enabled: false },
      { id: 'monitoramento', name: 'Monitoramento', icon: '🛰️', enabled: false },
      { id: 'monitoramentoVolkswagen', name: 'Monitoramento Volkswagen', icon: '🚗', enabled: false },
      { id: 'rotograma', name: 'Rotograma', icon: '🗺️', enabled: false },
      { id: 'checklist', name: 'Checklist Carretas', icon: '✅', enabled: false },
      { id: 'wms', name: 'WMS', icon: '📦', enabled: false },
      { id: 'portaria', name: 'Portaria', icon: '🛂', enabled: false },
      { id: 'controltower', name: 'Control Tower', icon: '📡', enabled: false },
      { id: 'levantamentoPosicoes', name: 'Levantamento Posições', icon: '📍', enabled: false },
      { id: 'etlBis', name: 'ETL BIs', icon: '🧪', enabled: false },
      { id: 'veiculosm', name: 'Veiculosm', icon: '🚚', enabled: false },
      { id: 'abastecimento', name: 'Abastecimento', icon: '⛽', enabled: false },
      { id: 'apis', name: 'APis', icon: '🔌', enabled: false },
      { id: 'bi', name: 'BI', icon: '📊', enabled: false },
    ];

    const userModules = availableModules
      .filter(module => user.modules.includes(module.id)) // Filtrar apenas módulos permitidos
      .map(module => ({
        ...module,
        enabled: true // Todos os módulos mostrados são habilitados
      }));
    setModules(userModules);
  }, [user.modules]);

  const handleModuleClick = (moduleId: string) => {
    setActiveModule(moduleId); // Como só mostramos módulos permitidos, não precisa verificar permissão
  };

  return (
    <div className="dashboard">
      <div className={`sidebar${sidebarVisible ? '' : ' sidebar-hidden'}`} style={{ position: 'relative' }}>
        {sidebarVisible && (
          <button
            className="sidebar-toggle-btn"
            onClick={() => setSidebarVisible(false)}
            aria-label="Esconder menu"
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              background: '#34495e',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: 32,
              height: 32,
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              zIndex: 10
            }}
          >
            ⮜
          </button>
        )}
        <div className="logo-section">
          <img src="/logo-fks.png" alt="FKS" className="logo" />
        </div>
        <div className="user-info">
          <h3>Olá, {user.nome || user.username}</h3>
        </div>
        <nav className="modules-nav">
          {modules.map(module => (
            <div
              key={module.id}
              className="module-item enabled"
              onClick={() => handleModuleClick(module.id)}
            >
              <span className="module-icon">{module.icon}</span>
              <span className="module-name">{module.name}</span>
              <span className="module-arrow">▼</span>
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="version">v1.10.21 - 01/10/2025</div>
          <button className="logout-btn" onClick={onLogout}>Sair</button>
        </div>
      </div>

      <div className={`main-content${sidebarVisible ? '' : ' main-content-expanded'}`}> 
        {!sidebarVisible && (
          <button
            className="sidebar-toggle-btn sidebar-toggle-btn-float"
            onClick={() => setSidebarVisible(true)}
            aria-label="Mostrar menu"
            style={{
              position: 'fixed',
              top: 24,
              left: 12,
              zIndex: 300,
              background: '#34495e',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: 38,
              height: 38,
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22
            }}
          >
            ⮞
          </button>
        )}
        {activeModule ? (
          <div className="module-content">
            {activeModule !== 'cadastro' && (
              <div className="module-header-nav">
                <button className="btn-voltar" onClick={() => setActiveModule(null)}>
                  ← Voltar ao Dashboard
                </button>
              </div>
            )}
            <div className="module-body">
              {activeModule === 'diario' && <DiarioModule />}
              {activeModule === 'diarioVeiculos' && <DiarioVeiculosModule />}
              {activeModule === 'cadastro' && <CadastroModule onBackToDashboard={() => setActiveModule(null)} />}
              {activeModule === 'patio' && <PatioModule />}
              {activeModule === 'rh' && <RhModule />}
              {activeModule === 'suporte' && <SuporteModule />}
              {activeModule === 'monitoramento' && <MonitoramentoModule userAccessType={user.nm_acesso} userSector={user.nm_setor} />}
              {activeModule === 'monitoramentoVolkswagen' && <MonitoramentoVolkswagenModule />}
              {activeModule === 'rotograma' && <RotogramaModule />}
              {activeModule === 'checklist' && <ChecklistModule />}
              {activeModule === 'wms' && <WMSModule />}
              {activeModule === 'portaria' && <PortariaModule />}
              {activeModule === 'controltower' && <ControlTowerModule />}
              {activeModule === 'levantamentoPosicoes' && <LevantamentoPosicoesModule />}
              {activeModule === 'etlBis' && <EtlBisModule />}
              {activeModule === 'veiculosm' && <VeiculosmModule />}
              {activeModule === 'abastecimento' && <AbastecimentoModule />}
              {activeModule === 'apis' && <ApisModule />}
              {activeModule === 'bi' && <BIModule />}
            </div>
          </div>
        ) : (
          <>
            <div className="content-header">
              <h1>Dashboard</h1>
            </div>
            <div className="content-body">
              <h2>Bem-vindo ao Sistema FKS!</h2>
              <p>Selecione um módulo no menu lateral para começar.</p>
              <div className="info-cards">
                <div className="info-card">
                  <h3>📊 Módulos Disponíveis</h3>
                  <p>Você tem acesso a {modules.length} módulos</p>
                </div>
                <div className="info-card">
                  <h3>👤 Usuário Logado</h3>
                  <p>{user.username}</p>
                </div>
                <div className="info-card">
                  <h3>🔧 Modo</h3>
                  <p>Produção (com backend)</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;